/**
 * Cloudflare API 클라이언트
 * Firebase 대신 Cloudflare Workers API를 사용
 */

/// <reference types="vite/client" />

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.your-domain.com';
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

export interface GoogleUser {
  id: string;
  email: string;
  displayName: string;
  photoURL: string;
}

export interface AuthResponse {
  token: string;
  user: GoogleUser;
}

/**
 * Google OAuth 로그인
 */
export async function signInWithGoogle(): Promise<GoogleUser | null> {
  try {
    console.log('🔄 Google OAuth 시작...');

    // Google OAuth 팝업 열기
    const redirectUri = `${window.location.origin}/auth/callback`;
    const scope = 'openid email profile';
    const responseType = 'code';
    
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${encodeURIComponent(GOOGLE_CLIENT_ID)}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=${responseType}&` +
      `scope=${encodeURIComponent(scope)}&` +
      `access_type=offline&` +
      `prompt=consent`;

    // 팝업 창 열기
    const popup = window.open(
      authUrl,
      'Google Login',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    if (!popup) {
      throw new Error('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
    }

    // 팝업에서 인증 코드 받기
    const code = await waitForPopupCallback(popup);

    if (!code) {
      throw new Error('인증 코드를 받지 못했습니다.');
    }

    // Cloudflare Workers API로 인증 코드 전송
    const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '인증 실패');
    }

    const authData: AuthResponse = await response.json();

    // 토큰 저장
    localStorage.setItem('auth_token', authData.token);
    localStorage.setItem('user', JSON.stringify(authData.user));

    console.log('✅ 로그인 성공:', authData.user);
    return authData.user;
  } catch (error) {
    console.error('🚫 로그인 실패:', error);
    throw error;
  }
}

/**
 * 팝업에서 OAuth 콜백 대기
 */
function waitForPopupCallback(popup: Window): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const checkInterval = setInterval(() => {
      try {
        if (popup.closed) {
          clearInterval(checkInterval);
          resolve(null);
          return;
        }

        // 팝업 URL 확인 (실제로는 postMessage 사용 권장)
        const popupUrl = popup.location.href;
        if (popupUrl.includes('auth/callback')) {
          const url = new URL(popupUrl);
          const code = url.searchParams.get('code');
          const error = url.searchParams.get('error');

          if (error) {
            clearInterval(checkInterval);
            popup.close();
            reject(new Error(error));
            return;
          }

          if (code) {
            clearInterval(checkInterval);
            popup.close();
            resolve(code);
            return;
          }
        }
      } catch (e) {
        // Cross-origin 에러는 무시 (정상적인 동작)
      }
    }, 100);

    // 타임아웃 (5분)
    setTimeout(() => {
      clearInterval(checkInterval);
      popup.close();
      reject(new Error('인증 시간이 초과되었습니다.'));
    }, 300000);
  });
}

/**
 * 로그아웃
 */
export async function signOutUser(): Promise<void> {
  try {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    console.log('✅ 로그아웃 완료');
  } catch (error) {
    console.error('🚫 로그아웃 실패:', error);
    throw error;
  }
}

/**
 * 현재 사용자 가져오기
 */
export function getCurrentUser(): GoogleUser | null {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    return JSON.parse(userStr);
  } catch {
    return null;
  }
}

/**
 * 인증 토큰 가져오기
 */
export function getAuthToken(): string | null {
  return localStorage.getItem('auth_token');
}

/**
 * 인증 상태 확인
 */
export function isAuthenticated(): boolean {
  return !!getAuthToken() && !!getCurrentUser();
}

/**
 * 인증 상태 변경 리스너 (간단한 구현)
 * 실제로는 CustomEvent를 사용하여 구현
 */
export function onAuthStateChange(callback: (user: GoogleUser | null) => void): () => void {
  const user = getCurrentUser();
  callback(user);

  // Storage 이벤트 리스너 (다른 탭에서 로그인/로그아웃 감지)
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === 'user' || e.key === 'auth_token') {
      const currentUser = getCurrentUser();
      callback(currentUser);
    }
  };

  window.addEventListener('storage', handleStorageChange);

  // 커스텀 이벤트 리스너 (같은 탭에서 로그인/로그아웃 감지)
  const handleAuthChange = () => {
    const currentUser = getCurrentUser();
    callback(currentUser);
  };

  window.addEventListener('auth-change', handleAuthChange);

  return () => {
    window.removeEventListener('storage', handleStorageChange);
    window.removeEventListener('auth-change', handleAuthChange);
  };
}

/**
 * API 요청 헬퍼
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAuthToken();

  if (!token) {
    throw new Error('인증 토큰이 없습니다.');
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.message || error.error || 'API 요청 실패');
  }

  return response.json();
}
